import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, PieChart, Pie, Cell, Area, AreaChart,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const C = { blue: "#0037ff", sky: "#0093e8", skyline: "#4dc3ff", forest: "#1d6f80",
  night: "#0a1230", panel: "#101b3f", panel2: "#152250", line: "rgba(148,170,255,.14)",
  ink: "#eef2ff", ink2: "#9aa7d4", ink3: "#5f6da3", alert: "#ff7a68", prev: "#3a4a86" };
const MIX_COLORS = ["#0037ff", "#0093e8", "#4dc3ff", "#1d6f80", "#6fd3e8", "#3a4a86", "#8ab4ff"];
const MESES_L = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const fmtK = (v) => {
  if (v == null) return "—";
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};
const fmtFull = (v) => (v == null ? "—" : `$${Math.round(v).toLocaleString("es-MX")}`);
const pct = (cur, prev) => (prev ? ((cur - prev) / Math.abs(prev)) * 100 : null);

const pad2 = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const periodDates = (period, hoy) => {
  const { y, m, d } = hoy;
  if (period === "quarter") { const qm = Math.floor((m - 1) / 3) * 3 + 1; return { desde: iso(y, qm, 1), hasta: iso(y, m, d) }; }
  if (period === "year") return { desde: iso(y, 1, 1), hasta: iso(y, m, d) };
  if (period === "12m") { let m0 = m - 11, y0 = y; if (m0 < 1) { m0 += 12; y0 -= 1; } return { desde: iso(y0, m0, 1), hasta: iso(y, m, d) }; }
  return { desde: iso(y, m, 1), hasta: iso(y, m, d) };
};
const monthDates = (y, m) => ({ desde: iso(y, m, 1), hasta: iso(y, m, new Date(y, m, 0).getDate()) });

const Delta = ({ cur, prev, pts = false, suffix = "" }) => {
  const d = pts ? (prev != null ? cur - prev : null) : pct(cur, prev);
  if (d == null) return <span className="dc-d" style={{ color: C.ink3 }}>—</span>;
  const up = d >= 0;
  return (
    <span className="dc-d" style={{ background: up ? "rgba(77,195,255,.13)" : "rgba(255,122,104,.13)", color: up ? C.skyline : C.alert }}>
      {up ? "▲" : "▼"} {Math.abs(d).toFixed(1)}{pts ? " pts" : "%"}{suffix}
    </span>
  );
};

const Panel = ({ title, sub, children, className = "" }) => (
  <div className={`rounded-2xl p-5 ${className}`} style={{ background: C.panel, border: `1px solid ${C.line}` }}>
    {title && (
      <div className="flex justify-between items-baseline mb-4 gap-2">
        <h3 className="text-[14.5px] font-bold" style={{ color: C.ink }}>{title}</h3>
        {sub && <span className="text-[11px] text-right" style={{ color: C.ink3 }}>{sub}</span>}
      </div>
    )}
    {children}
  </div>
);

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs" style={{ background: C.panel2, border: "1px solid rgba(148,170,255,.25)", color: C.ink }}>
      <p className="font-bold mb-1">{label}</p>
      {payload.filter((p) => p.value != null).map((p, i) => (
        <p key={i}><span style={{ color: p.color || p.stroke }}>{p.name}</span>: {fmtK(p.value)}</p>
      ))}
    </div>
  );
};

export default function DashboardComercial() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [compare, setCompare] = useState("yoy");
  const [filtros, setFiltros] = useState({ canal: "", agente: "", servicio: "" });
  const [customRange, setCustomRange] = useState({ desde: "", hasta: "" });
  const [secs, setSecs] = useState(0);
  const [detail, setDetail] = useState(null);
  const timerRef = useRef(null);

  const openDetail = useCallback(async (title, sub, range, dims = {}) => {
    setDetail({ title, sub, loading: true });
    try {
      const res = await axios.get(`${API}/dashboard-comercial/detalle`, {
        params: { ...range, ...filtros, ...dims }, withCredentials: true,
      });
      setDetail({ title, sub, loading: false, ...res.data });
    } catch {
      setDetail({ title, sub, loading: false, error: true });
    }
  }, [filtros]);

  const fetchData = useCallback(async (silent = false) => {
    if (period === "custom" && (!customRange.desde || !customRange.hasta)) return;
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard-comercial/data`, {
        params: { period, compare, ...filtros, ...(period === "custom" ? customRange : {}) }, withCredentials: true,
      });
      setData(res.data); setError(null); setSecs(0);
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo conectar con el servidor";
      if (!data) setError(msg);
    } finally { setLoading(false); }
  }, [period, compare, filtros, customRange]); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    document.body.classList.add("pd-dark-page");
    return () => document.body.classList.remove("pd-dark-page");
  }, []);
  useEffect(() => {
    timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    const rf = setInterval(() => fetchData(true), 30000);
    return () => { clearInterval(timerRef.current); clearInterval(rf); };
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: C.night }} data-testid="dc-loading">
        <img src="/pd-logo-white.png" alt="PD Travel" className="h-10 opacity-90 animate-pulse" />
        <p className="text-sm" style={{ color: C.ink2 }}>Cargando dashboard…</p>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: C.night }} data-testid="dc-error">
        <img src="/pd-logo-white.png" alt="PD Travel" className="h-10 opacity-90" />
        <p className="text-sm max-w-md" style={{ color: C.ink2 }}>{String(error)}</p>
        <button onClick={() => fetchData()} className="px-5 py-2 rounded-full text-sm font-bold text-white" style={{ background: `linear-gradient(115deg,${C.blue},${C.sky})` }} data-testid="dc-retry">Reintentar</button>
      </div>
    );
  }

  const d = data;
  const canSeeUtl = d.perms?.see_utl_amounts !== false;
  const metaMes = d.meta_mes?.venta || 0;
  const avanceMeta = metaMes ? Math.min(1.5, d.tabla.cur.venta / metaMes) : 0;
  const faltaMeta = Math.max(0, metaMes - d.tabla.cur.venta);
  const diasRest = d.dias_mes - d.hoy.d;
  const gaugeC = 2 * Math.PI * 64;

  const trendData = d.monthly12.labels.map((l, i) => ({ name: l, Venta: d.monthly12.venta[i], Utilidad: d.monthly12.utilidad[i], Meta: d.monthly12.meta[i] }));
  const paceData = d.pace.labels.map((l, i) => ({ name: l, [`${d.pace.mes_label} ${d.hoy.y}`]: d.pace.cum_cur[i], [`${d.pace.mes_label} ${d.hoy.y - 1}`]: d.pace.cum_prev[i], Meta: d.pace.meta[i] }));
  const yoyData = d.yoy_monthly.labels.map((l, i) => ({ name: l, [d.yoy_monthly.y_cur]: d.yoy_monthly.cur[i], [d.yoy_monthly.y_prev]: d.yoy_monthly.prev[i] }));
  const mixTotal = d.mix.reduce((s, m) => s + m.venta, 0) || 1;
  const maxCanal = canSeeUtl
    ? Math.max(1, ...d.canales.map((c) => c.utilidad || 0), ...d.canales.map((c) => c.utilidad_prev || 0))
    : Math.max(1, ...d.canales.map((c) => c.venta || 0), ...d.canales.map((c) => c.venta_prev || 0));
  const paceIdx = Math.max(0, d.pace.dia_hoy - 1);
  const paceDiff = (d.pace.cum_cur[paceIdx] ?? 0) - (d.pace.cum_prev[paceIdx] ?? 0);
  const sinDatos = !d.cur.folios && !d.acum.folios;

  const setPeriodo = (val) => {
    if (val === "custom" && (!customRange.desde || !customRange.hasta)) {
      const h = new Date();
      setCustomRange({ desde: iso(h.getFullYear(), h.getMonth() + 1, 1), hasta: iso(h.getFullYear(), h.getMonth() + 1, h.getDate()) });
    }
    setPeriod(val);
  };
  const curRange = () => (period === "custom" && customRange.desde && customRange.hasta ? customRange : periodDates(period, data.hoy));

  const segBtn = (val, lbl) => (
    <button key={val} onClick={() => setPeriodo(val)} data-testid={`dc-period-${val}`}
      className="px-4 py-2 rounded-lg text-[12.5px] font-bold transition-all"
      style={period === val ? { background: `linear-gradient(115deg,${C.blue},${C.sky})`, color: "#fff" } : { color: C.ink2 }}>
      {lbl}
    </button>
  );
  const selStyle = { background: C.panel2, color: C.ink2, border: `1px solid ${C.line}`, borderRadius: 10, padding: "8px 13px", fontSize: 12.5, fontWeight: 700 };

  return (
    <div className="min-h-screen" style={{ background: C.night, fontFamily: "'Mulish', sans-serif", color: C.ink }} data-testid="dashboard-comercial">
      <div className="max-w-[1220px] mx-auto px-4 sm:px-8 py-6 pb-12">
        {/* Topbar */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-baseline gap-3.5">
            <img src="/pd-logo-white.png" alt="PD Travel" className="h-7 self-center" />
            <small className="text-xs pl-3.5" style={{ color: C.ink3, borderLeft: `1px solid ${C.line}` }}>Dashboard comercial · Ventas Generales</small>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs" style={{ color: C.ink2 }} data-testid="dc-sync">Sincronizado con Google Sheets · hace {secs} s</span>
            {d.stale ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3.5 py-1.5" style={{ color: C.alert, border: "1px solid rgba(255,122,104,.4)", background: "rgba(255,122,104,.08)" }} data-testid="dc-stale">Datos de hace unos minutos</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3.5 py-1.5" style={{ color: C.skyline, border: "1px solid rgba(77,195,255,.35)", background: "rgba(0,147,232,.08)" }}>
                <span className="w-[7px] h-[7px] rounded-full animate-pulse" style={{ background: C.skyline }} /> En vivo
              </span>
            )}
          </div>
        </div>

        {/* Barra de periodo */}
        <div className="flex items-center gap-3.5 flex-wrap rounded-2xl px-4 py-3 mb-5" style={{ background: C.panel, border: `1px solid ${C.line}` }}>
          <span className="text-[11px] font-bold tracking-[1.5px] uppercase" style={{ color: C.ink3 }}>Periodo</span>
          <div className="inline-flex rounded-xl p-[3px]" style={{ background: "rgba(148,170,255,.08)" }}>
            {segBtn("month", "Este mes")}{segBtn("quarter", "Trimestre")}{segBtn("year", "Año")}{segBtn("12m", "12 meses")}{segBtn("custom", "Personalizado")}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-2" data-testid="dc-custom-range">
              <input type="date" value={customRange.desde} max={customRange.hasta || undefined}
                onChange={(e) => setCustomRange({ ...customRange, desde: e.target.value })}
                className="dc-date" style={selStyle} data-testid="dc-fecha-desde" />
              <span className="text-[11px] font-bold" style={{ color: C.ink3 }}>a</span>
              <input type="date" value={customRange.hasta} min={customRange.desde || undefined}
                onChange={(e) => setCustomRange({ ...customRange, hasta: e.target.value })}
                className="dc-date" style={selStyle} data-testid="dc-fecha-hasta" />
            </div>
          )}
          <div className="flex items-center gap-2.5 ml-auto flex-wrap">
            <span className="text-[11px] font-bold tracking-[1.5px] uppercase" style={{ color: C.ink3 }}>Comparar con</span>
            <select value={compare} onChange={(e) => setCompare(e.target.value)} style={selStyle} data-testid="dc-compare">
              <option value="yoy">Mismo periodo año pasado (YoY)</option>
              <option value="mom">Periodo anterior (MoM)</option>
              <option value="y2">Mismo periodo hace 2 años</option>
              <option value="none">Sin comparativo</option>
            </select>
            <select value={filtros.canal} onChange={(e) => setFiltros({ ...filtros, canal: e.target.value })} style={selStyle} data-testid="dc-filtro-canal">
              <option value="">Canal: todos</option>
              {d.filtros.canales.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filtros.agente} onChange={(e) => setFiltros({ ...filtros, agente: e.target.value })} style={selStyle} data-testid="dc-filtro-agente">
              <option value="">Agente: todos</option>
              {d.filtros.agentes.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={filtros.servicio} onChange={(e) => setFiltros({ ...filtros, servicio: e.target.value })} style={selStyle} data-testid="dc-filtro-servicio">
              <option value="">Servicio: todos</option>
              {d.filtros.servicios.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {sinDatos && (
          <div className="rounded-2xl p-4 mb-5 text-sm" style={{ background: C.panel, border: `1px solid ${C.line}`, color: C.ink2 }} data-testid="dc-sin-datos">
            Sin ventas en el periodo con los filtros seleccionados.
          </div>
        )}

        {/* Hero */}
        <section className="relative overflow-hidden rounded-[20px] px-6 sm:px-8 py-7 mb-3.5 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-7 items-center"
          style={{ background: `linear-gradient(115deg,${C.blue} 0%,${C.sky} 62%,${C.skyline} 100%)` }} data-testid="dc-hero">
          <div className="absolute rounded-full pointer-events-none" style={{ width: 560, height: 560, right: -160, top: -290, border: "1.5px solid rgba(255,255,255,.2)" }} />
          <div className="absolute rounded-full pointer-events-none" style={{ width: 360, height: 360, right: -60, top: -190, border: "1.5px solid rgba(255,255,255,.2)" }} />
          <div className="cursor-pointer" onClick={() => openDetail(`Venta · ${d.period_label}`, "Folios que componen la venta del periodo", curRange())} title="Clic para ver los folios" data-testid="dc-hero-venta">
            <div className="text-[11px] font-bold tracking-[2.5px] uppercase mb-2" style={{ color: "rgba(255,255,255,.75)" }}>Venta · {d.period_label}</div>
            <div className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="dc-venta-periodo">{fmtFull(d.cur.venta)} <span className="text-xl font-light" style={{ color: "rgba(255,255,255,.8)" }}>MXN</span></div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {d.cmp && <span className="dc-chip" style={{ color: pct(d.cur.venta, d.cmp.venta) >= 0 ? "#a6f0ff" : "#ffc4ba" }}>{pct(d.cur.venta, d.cmp.venta) == null ? "—" : `${pct(d.cur.venta, d.cmp.venta) >= 0 ? "▲" : "▼"} ${Math.abs(pct(d.cur.venta, d.cmp.venta)).toFixed(1)}%`} <small>vs {d.compare_label}</small></span>}
              {d.cmp && <span className="dc-chip">{fmtK(d.cmp.venta)} <small>{d.compare_label}</small></span>}
            </div>
          </div>
          <div className={canSeeUtl ? "cursor-pointer" : ""} onClick={canSeeUtl ? () => openDetail(`Utilidad operativa · ${d.period_label}`, "Folios que componen la utilidad del periodo", curRange()) : undefined} title={canSeeUtl ? "Clic para ver los folios" : ""} data-testid="dc-hero-utilidad">
            <div className="text-[11px] font-bold tracking-[2.5px] uppercase mb-2" style={{ color: "rgba(255,255,255,.75)" }}>
              {canSeeUtl ? `Utilidad operativa · ${d.period_label}` : `Margen operativo · ${d.period_label}`}
            </div>
            {canSeeUtl ? (
              <>
                <div className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="dc-utilidad-periodo">{fmtFull(d.cur.utilidad)} <span className="text-xl font-light" style={{ color: "rgba(255,255,255,.8)" }}>MXN</span></div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {d.cmp && <span className="dc-chip" style={{ color: pct(d.cur.utilidad, d.cmp.utilidad) >= 0 ? "#a6f0ff" : "#ffc4ba" }}>{pct(d.cur.utilidad, d.cmp.utilidad) == null ? "—" : `${pct(d.cur.utilidad, d.cmp.utilidad) >= 0 ? "▲" : "▼"} ${Math.abs(pct(d.cur.utilidad, d.cmp.utilidad)).toFixed(1)}%`} <small>vs {d.compare_label}</small></span>}
                  <span className="dc-chip">{d.cur.margen}% <small>margen</small></span>
                </div>
              </>
            ) : (
              <>
                <div className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="dc-margen-periodo">{d.cur.margen}<span className="text-xl font-light" style={{ color: "rgba(255,255,255,.8)" }}>%</span></div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {d.cmp && <span className="dc-chip" style={{ color: (d.cur.margen - d.cmp.margen) >= 0 ? "#a6f0ff" : "#ffc4ba" }}>{d.cmp.margen == null ? "—" : `${(d.cur.margen - d.cmp.margen) >= 0 ? "▲" : "▼"} ${Math.abs(d.cur.margen - d.cmp.margen).toFixed(1)} pts`} <small>vs {d.compare_label}</small></span>}
                  <span className="dc-chip"><small>margen = utilidad ÷ venta</small></span>
                </div>
              </>
            )}
          </div>
          <div className="relative w-[150px] h-[150px] mx-auto z-[1]" data-testid="dc-gauge">
            <svg width="150" height="150" viewBox="0 0 150 150" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="75" cy="75" r="64" fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="10" />
              <circle cx="75" cy="75" r="64" fill="none" stroke="#fff" strokeWidth="10" strokeLinecap="round"
                strokeDasharray={gaugeC} strokeDashoffset={gaugeC * (1 - Math.min(1, avanceMeta))} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <strong className="text-[28px] font-black">{metaMes ? `${Math.round(avanceMeta * 100)}%` : "—"}</strong>
              <em className="not-italic text-[10.5px] mt-0.5" style={{ color: "rgba(255,255,255,.85)" }}>
                {metaMes ? <>de la meta del mes<br />faltan {fmtK(faltaMeta)} · {diasRest} días</> : "sin meta configurada"}
              </em>
            </div>
          </div>
        </section>

        {/* Tabla ejecutiva */}
        <div className="rounded-2xl px-6 pb-2.5 pt-1 mb-5 overflow-x-auto" style={{ background: C.panel, border: `1px solid ${C.line}` }} data-testid="dc-tabla">
          <table className="w-full border-collapse text-[12.5px] min-w-[760px]">
            <thead><tr>
              {["Indicador", d.tabla.label_cur, d.tabla.mom_label, "vs MoM", d.tabla.yoy_label, "vs YoY", `Acum. ${d.hoy.y}`, `Acum. ${d.hoy.y - 1}`].map((h, i) => (
                <th key={i} className={`text-[10.5px] font-bold tracking-[1.2px] uppercase pt-3.5 pb-2.5 ${i === 0 ? "text-left" : "text-right pl-4"}`} style={{ color: C.ink3 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[
                { l: "Venta", f: (a) => fmtK(a.venta), k: "venta" },
                { l: "Utilidad operativa", f: (a) => fmtK(a.utilidad), k: "utilidad" },
                { l: "Margen", f: (a) => `${a.margen}%`, k: "margen", pts: true },
                { l: "Folios / PAX", f: (a) => `${a.folios} / ${a.pax}`, k: "folios" },
                { l: "Ticket promedio", f: (a) => fmtFull(a.ticket), k: "ticket" },
              ].map((row) => (
                <tr key={row.l}>
                  <td className="text-left font-bold py-2.5" style={{ color: C.ink, borderTop: `1px solid ${C.line}` }}>{row.l}</td>
                  <td className="dc-td font-black" style={{ color: C.ink, fontSize: 13.5 }}>{row.f(d.tabla.cur)}</td>
                  <td className="dc-td">{row.f(d.tabla.mom)}</td>
                  <td className="dc-td"><Delta cur={d.tabla.cur[row.k]} prev={d.tabla.mom[row.k]} pts={row.pts} /></td>
                  <td className="dc-td">{row.f(d.tabla.yoy)}</td>
                  <td className="dc-td"><Delta cur={d.tabla.cur[row.k]} prev={d.tabla.yoy[row.k]} pts={row.pts} /></td>
                  <td className="dc-td font-black" style={{ color: C.ink }}>{row.f(d.acum)}</td>
                  <td className="dc-td">{row.f(d.acum_prev)} <Delta cur={d.acum[row.k]} prev={d.acum_prev[row.k]} pts={row.pts} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-5" data-testid="dc-kpis">
          {[
            { l: `Venta acumulada ${d.hoy.y}`, v: fmtK(d.acum.venta), c: <><span><Delta cur={d.acum.venta} prev={d.acum_prev.venta} /> vs mismo periodo {d.hoy.y - 1}</span></>,
              click: () => openDetail(`Venta acumulada ${d.hoy.y}`, "Folios del 1 de enero a hoy", { desde: iso(d.hoy.y, 1, 1), hasta: iso(d.hoy.y, d.hoy.m, d.hoy.d) }), tid: "dc-kpi-venta-acum" },
            canSeeUtl
              ? { l: `Utilidad acumulada ${d.hoy.y}`, v: fmtK(d.acum.utilidad), c: <><span><Delta cur={d.acum.utilidad} prev={d.acum_prev.utilidad} /> vs mismo periodo {d.hoy.y - 1}</span><span>margen <b style={{ color: C.skyline }}>{d.acum.margen}%</b></span></>,
                  click: () => openDetail(`Utilidad acumulada ${d.hoy.y}`, "Folios del 1 de enero a hoy", { desde: iso(d.hoy.y, 1, 1), hasta: iso(d.hoy.y, d.hoy.m, d.hoy.d) }), tid: "dc-kpi-utilidad-acum" }
              : { l: `Margen acumulado ${d.hoy.y}`, v: `${d.acum.margen}%`, c: <><span><Delta cur={d.acum.margen} prev={d.acum_prev.margen} pts /> vs mismo periodo {d.hoy.y - 1}</span><span>utilidad ÷ venta del año</span></>,
                  click: undefined, tid: "dc-kpi-margen-acum" },
            { l: `Proyección de cierre ${d.pace.mes_label.toLowerCase()}`, v: fmtK(d.proyeccion), c: <><span>al ritmo actual de venta diaria</span>{metaMes > 0 && <span><b style={{ color: d.proyeccion >= metaMes ? C.skyline : C.alert }}>{Math.round(d.proyeccion / metaMes * 100)}%</b> de la meta del mes</span>}</>,
              click: () => openDetail(`Base de la proyección · ${MESES_L[d.hoy.m]} ${d.hoy.y}`, "Folios del mes en curso, base del cálculo de la proyección", { desde: iso(d.hoy.y, d.hoy.m, 1), hasta: iso(d.hoy.y, d.hoy.m, d.hoy.d) }), tid: "dc-kpi-proyeccion" },
            { l: `CxC pendiente ${d.hoy.y}`, v: fmtK(d.cxc.pendiente), c: <><span><b style={{ color: d.cxc.vencidos ? C.alert : C.skyline }}>{d.cxc.vencidos} folios vencidos</b> · {fmtK(d.cxc.vencidos_monto)}</span></>,
              click: () => openDetail(`CxC pendiente ${d.hoy.y}`, "Folios con saldo pendiente de cobro en el año", { desde: iso(d.hoy.y, 1, 1), hasta: iso(d.hoy.y, 12, 31) }, { cxc: 1 }), tid: "dc-kpi-cxc" },
          ].map((k) => (
            <div key={k.l} onClick={k.click} title={k.click ? "Clic para ver los folios" : ""} data-testid={k.tid}
              className={`rounded-2xl px-5 py-4 transition-transform ${k.click ? "hover:-translate-y-0.5 cursor-pointer" : ""}`} style={{ background: C.panel, border: `1px solid ${C.line}` }}>
              <div className="text-xs" style={{ color: C.ink2 }}>{k.l}</div>
              <div className="text-[27px] font-black tracking-tight mt-1.5">{k.v}</div>
              <div className="flex flex-col gap-1 mt-2 text-[11.5px]" style={{ color: C.ink3 }}>{k.c}</div>
            </div>
          ))}
        </div>

        {/* Tendencia + ritmo */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-3.5 mb-3.5">
          <Panel title={canSeeUtl ? "Tendencia mensual · venta, utilidad y meta" : "Tendencia mensual · venta y meta"} sub="últimos 12 meses · MXN">
            <div style={{ height: 250, cursor: "pointer" }} data-testid="dc-chart-trend">
              <ResponsiveContainer>
                <ComposedChart data={trendData} onClick={(e) => {
                  if (e?.activeTooltipIndex == null) return;
                  const k = 11 - e.activeTooltipIndex;
                  let m0 = d.hoy.m - k, y0 = d.hoy.y;
                  if (m0 < 1) { m0 += 12; y0 -= 1; }
                  openDetail(`Ventas de ${MESES_L[m0]} ${y0}`, "Folios registrados en el mes", monthDates(y0, m0));
                }}>
                  <CartesianGrid stroke="rgba(148,170,255,.08)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: C.ink3, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtK} tick={{ fill: C.ink3, fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
                  <Tooltip content={<TT />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  <Bar dataKey="Venta" fill={C.sky} radius={[6, 6, 0, 0]} maxBarSize={26} />
                  {canSeeUtl && <Line dataKey="Utilidad" stroke="#6fd3e8" strokeWidth={2.5} dot={false} type="monotone" />}
                  <Line dataKey="Meta" stroke="#ffffff" strokeWidth={1.5} strokeDasharray="6 5" dot={false} type="monotone" connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title="Ritmo del mes" sub={`venta acumulada diaria · ${d.pace.mes_label.toLowerCase()}`}>
            <div style={{ height: 220, cursor: "pointer" }} data-testid="dc-chart-pace">
              <ResponsiveContainer>
                <AreaChart data={paceData} onClick={(e) => {
                  const day = Number(e?.activeLabel);
                  if (!day || day > d.hoy.d) return;
                  const f = iso(d.hoy.y, d.hoy.m, day);
                  openDetail(`Ventas del ${day} de ${MESES_L[d.hoy.m].toLowerCase()} ${d.hoy.y}`, "Folios registrados ese día", { desde: f, hasta: f });
                }}>
                  <CartesianGrid stroke="rgba(148,170,255,.08)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: C.ink3, fontSize: 10.5 }} axisLine={false} tickLine={false} interval={4} />
                  <YAxis tickFormatter={fmtK} tick={{ fill: C.ink3, fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
                  <Tooltip content={<TT />} />
                  <Area dataKey={`${d.pace.mes_label} ${d.hoy.y}`} stroke={C.skyline} fill="rgba(0,147,232,.18)" strokeWidth={2.5} dot={false} type="monotone" />
                  <Line dataKey={`${d.pace.mes_label} ${d.hoy.y - 1}`} stroke={C.prev} strokeWidth={2} strokeDasharray="5 4" dot={false} type="monotone" />
                  <Line dataKey="Meta" stroke="rgba(255,255,255,.5)" strokeWidth={1.5} strokeDasharray="2 4" dot={false} type="monotone" connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] mt-2.5" style={{ color: C.ink3 }}>
              Al día {d.pace.dia_hoy}, {d.pace.mes_label.toLowerCase()} {d.hoy.y} va <b style={{ color: paceDiff >= 0 ? C.skyline : C.alert }}>{fmtK(Math.abs(paceDiff))} {paceDiff >= 0 ? "arriba" : "abajo"}</b> de {d.pace.mes_label.toLowerCase()} {d.hoy.y - 1}.
            </div>
          </Panel>
        </div>

        {/* YoY + canal/mix */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mb-3.5">
          <Panel title="Comparativo anual por mes" sub={`venta ${d.yoy_monthly.y_cur} vs ${d.yoy_monthly.y_prev}`}>
            <div style={{ height: 210 }} data-testid="dc-chart-yoy">
              <ResponsiveContainer>
                <BarChart data={yoyData}>
                  <CartesianGrid stroke="rgba(148,170,255,.08)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: C.ink3, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtK} tick={{ fill: C.ink3, fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
                  <Tooltip content={<TT />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  <Bar dataKey={String(d.yoy_monthly.y_cur)} fill={C.sky} radius={[5, 5, 0, 0]} maxBarSize={20} cursor="pointer"
                    onClick={(_, i) => openDetail(`Ventas de ${MESES_L[i + 1]} ${d.yoy_monthly.y_cur}`, "Folios registrados en el mes", monthDates(d.yoy_monthly.y_cur, i + 1))} />
                  <Bar dataKey={String(d.yoy_monthly.y_prev)} fill={C.prev} radius={[5, 5, 0, 0]} maxBarSize={20} cursor="pointer"
                    onClick={(_, i) => openDetail(`Ventas de ${MESES_L[i + 1]} ${d.yoy_monthly.y_prev}`, "Folios registrados en el mes", monthDates(d.yoy_monthly.y_prev, i + 1))} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title={canSeeUtl ? "Utilidad por canal" : "Venta por canal"} sub={`${d.period_label}${d.compare_label ? ` · marca vs ${d.compare_label}` : ""}`}>
            <div data-testid="dc-canales">
              {d.canales.length === 0 && <p className="text-xs" style={{ color: C.ink3 }}>Sin datos en el periodo.</p>}
              {d.canales.slice(0, 6).map((c, i) => {
                const val = canSeeUtl ? c.utilidad : c.venta;
                const valPrev = canSeeUtl ? c.utilidad_prev : c.venta_prev;
                return (
                <div key={c.nombre} className="mb-3 cursor-pointer" title="Clic para ver los folios"
                  onClick={() => openDetail(`Canal: ${c.nombre} · ${d.period_label}`, "Folios del canal en el periodo", curRange(), { canal: c.nombre })}>
                  <div className="flex justify-between text-[12.5px] mb-1 items-center">
                    <b>{c.nombre}</b>
                    <span style={{ color: C.ink2 }}>{fmtK(val)} {valPrev > 0 && <Delta cur={val} prev={valPrev} />}</span>
                  </div>
                  <div className="relative h-[9px] rounded-md overflow-visible" style={{ background: "rgba(148,170,255,.1)" }}>
                    <i className="block h-full rounded-md" style={{ width: `${Math.max(2, (val || 0) / maxCanal * 100)}%`, background: i === 0 ? `linear-gradient(90deg,${C.blue},${C.sky})` : [C.sky, C.skyline, C.forest, "#6fd3e8", C.prev][i - 1] || C.prev }} />
                    {valPrev > 0 && <em className="absolute -top-0.5 -bottom-0.5 w-[2px]" style={{ left: `${Math.min(99, valPrev / maxCanal * 100)}%`, background: "rgba(255,255,255,.55)" }} />}
                  </div>
                </div>
              );})}
              {d.canales.some((c) => (canSeeUtl ? c.utilidad_prev : c.venta_prev) > 0) && <div className="text-[11px]" style={{ color: C.ink3 }}>La línea blanca marca el nivel de {d.compare_label}.</div>}
            </div>
            <div className="flex justify-between items-baseline mt-5 mb-3">
              <h3 className="text-[14.5px] font-bold">Mix de servicios</h3>
              <span className="text-[11px]" style={{ color: C.ink3 }}>% de venta · {d.period_label}</span>
            </div>
            <div style={{ height: 165 }} data-testid="dc-chart-mix">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={d.mix.slice(0, 7).map((m) => ({ name: m.nombre, value: Math.round(m.venta / mixTotal * 100) }))}
                    dataKey="value" innerRadius="62%" outerRadius="90%" paddingAngle={1} stroke={C.panel} strokeWidth={3} cursor="pointer"
                    onClick={(slice) => slice?.name && openDetail(`Servicio: ${slice.name} · ${d.period_label}`, "Folios del servicio en el periodo", curRange(), { servicio: slice.name })}>
                    {d.mix.slice(0, 7).map((_, i) => <Cell key={i} fill={MIX_COLORS[i % MIX_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={{ background: C.panel2, border: "1px solid rgba(148,170,255,.25)", borderRadius: 8, fontSize: 12 }} itemStyle={{ color: C.ink }} labelStyle={{ color: C.ink }} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconSize={9} wrapperStyle={{ fontSize: 11, color: C.ink2 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Tops + CxC */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
          <Panel title="Top agentes" sub={`${d.period_label}${d.compare_label ? ` · vs ${d.compare_label}` : ""}`}>
            <ul data-testid="dc-top-agentes">
              {d.top_agentes.length === 0 && <li className="text-xs py-2" style={{ color: C.ink3 }}>Sin datos en el periodo.</li>}
              {d.top_agentes.map((a, i) => (
                <li key={a.nombre} className="flex justify-between items-center py-2.5 text-[13px] cursor-pointer hover:bg-white/[.03] rounded-md px-1 -mx-1" title="Clic para ver los folios"
                  onClick={() => openDetail(`Agente: ${a.nombre} · ${d.period_label}`, "Folios del agente en el periodo", curRange(), { agente: a.nombre })}
                  style={{ borderBottom: i < d.top_agentes.length - 1 ? `1px solid ${C.line}` : "none" }}>
                  <span><span className="dc-rank">{i + 1}</span>{a.nombre}</span>
                  <span className="font-bold" style={{ color: C.ink2 }}>{fmtK(a.venta)} {a.venta_prev > 0 && <Delta cur={a.venta} prev={a.venta_prev} />}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Top clientes y socios" sub={d.period_label}>
            <ul data-testid="dc-top-clientes">
              {d.top_clientes.length === 0 && <li className="text-xs py-2" style={{ color: C.ink3 }}>Sin datos en el periodo.</li>}
              {d.top_clientes.map((c, i) => (
                <li key={c.nombre} className="flex justify-between items-center py-2.5 text-[13px] cursor-pointer hover:bg-white/[.03] rounded-md px-1 -mx-1" title="Clic para ver los folios"
                  onClick={() => openDetail(`Cliente: ${c.nombre} · ${d.period_label}`, "Folios del cliente en el periodo", curRange(), { cliente: c.nombre })}
                  style={{ borderBottom: i < d.top_clientes.length - 1 ? `1px solid ${C.line}` : "none" }}>
                  <span><span className="dc-rank">{i + 1}</span>{c.nombre}</span>
                  <span className="font-bold" style={{ color: C.ink2 }}>{fmtK(c.venta)}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Cuentas por cobrar" sub={`vencimientos · ${d.hoy.y}`}>
            <ul data-testid="dc-cxc">
              {d.cxc.lista.length === 0 && <li className="text-xs py-2" style={{ color: C.ink3 }}>Sin saldos pendientes. 🎉</li>}
              {d.cxc.lista.map((x, i) => (
                <li key={x.folio + i} className="flex justify-between items-center py-2.5 text-[12.5px] gap-2" style={{ borderBottom: i < d.cxc.lista.length - 1 ? `1px solid ${C.line}` : "none" }}>
                  <span className="truncate">{x.folio} · {x.cliente || "—"}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-bold" style={{ color: C.ink2 }}>{fmtK(x.monto)}</span>
                    <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={x.vencido ? { background: "rgba(255,122,104,.14)", color: C.alert } : { background: "rgba(77,195,255,.12)", color: C.skyline }}>
                      {x.vencido ? "Vencido" : (x.status || "Por vencer")}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        <footer className="mt-8 flex justify-between items-center text-xs flex-wrap gap-2" style={{ color: C.ink3 }}>
          <span className="font-light">Expertos en viajes y eventos de negocios.</span>
          <span>pdtravelmex.com · datos en vivo del Sheet de ventas</span>
        </footer>
      </div>

      {/* Modal de detalle (drill-down) */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" style={{ background: C.panel, color: C.ink, border: "1px solid rgba(148,170,255,.28)", fontFamily: "'Mulish', sans-serif" }} data-testid="dc-detalle-modal">
          <DialogHeader className="px-6 pt-5 pb-3" style={{ borderBottom: `1px solid ${C.line}` }}>
            <DialogTitle className="text-[16px] font-black" style={{ color: C.ink }} data-testid="dc-detalle-titulo">{detail?.title}</DialogTitle>
            <p className="text-[11.5px]" style={{ color: C.ink3 }}>
              {detail?.sub} · fuente: Google Sheet "Base de Ventas"
              {(filtros.canal || filtros.agente || filtros.servicio) && <> · filtros activos: {[filtros.canal, filtros.agente, filtros.servicio].filter(Boolean).join(", ")}</>}
            </p>
          </DialogHeader>
          {detail?.loading ? (
            <div className="py-14 text-center text-sm" style={{ color: C.ink2 }}>Cargando folios…</div>
          ) : detail?.error ? (
            <div className="py-14 text-center text-sm" style={{ color: C.alert }}>No se pudo cargar el detalle. Intenta de nuevo.</div>
          ) : detail && (
            <>
              <div className="flex flex-wrap gap-2 px-6 py-3" style={{ borderBottom: `1px solid ${C.line}` }} data-testid="dc-detalle-totales">
                {[
                  ...(detail.cxc_total != null ? [["CxC pendiente", fmtFull(detail.cxc_total)]] : []),
                  ["Venta", fmtFull(detail.total?.venta)],
                  ...(canSeeUtl ? [["Utilidad", fmtFull(detail.total?.utilidad)]] : []),
                  ["Margen", `${detail.total?.margen ?? 0}%`],
                  ["Folios", detail.total?.folios ?? 0],
                  ["PAX", detail.total?.pax ?? 0],
                ].map(([l, v]) => (
                  <span key={l} className="text-[11.5px] font-bold px-3 py-1.5 rounded-full" style={{ background: C.panel2, color: C.ink2, border: `1px solid ${C.line}` }}>
                    {l}: <b style={{ color: C.ink }}>{v}</b>
                  </span>
                ))}
              </div>
              <div className="overflow-auto max-h-[52vh] px-6 pb-5">
                {(!detail.rows || detail.rows.length === 0) ? (
                  <p className="text-sm py-8 text-center" style={{ color: C.ink3 }}>Sin folios en este rango con los filtros seleccionados.</p>
                ) : (
                  <table className="w-full border-collapse text-[12px] min-w-[720px]" data-testid="dc-detalle-tabla">
                    <thead><tr>
                      {["Folio", "Fecha", "Cliente", "Agente", "Servicio", "Canal", "PAX", "Venta", ...(canSeeUtl ? ["Utilidad"] : []), ...(detail.cxc_total != null ? ["Pendiente", "Status"] : [])].map((h, i) => (
                        <th key={h} className={`sticky top-0 text-[10px] font-bold tracking-[1px] uppercase py-2.5 ${i >= 6 ? "text-right" : "text-left"}`} style={{ color: C.ink3, background: C.panel, borderBottom: `1px solid ${C.line}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {detail.rows.map((r, i) => (
                        <tr key={r.folio + i}>
                          <td className="py-2 pr-2 font-bold" style={{ color: C.skyline, borderBottom: `1px solid ${C.line}` }}>{r.folio}</td>
                          <td className="py-2 pr-2" style={{ color: C.ink2, borderBottom: `1px solid ${C.line}` }}>{r.fecha}</td>
                          <td className="py-2 pr-2 max-w-[180px] truncate" style={{ color: C.ink, borderBottom: `1px solid ${C.line}` }}>{r.cliente || "—"}</td>
                          <td className="py-2 pr-2" style={{ color: C.ink2, borderBottom: `1px solid ${C.line}` }}>{r.agente || "—"}</td>
                          <td className="py-2 pr-2" style={{ color: C.ink2, borderBottom: `1px solid ${C.line}` }}>{r.servicio || "—"}</td>
                          <td className="py-2 pr-2" style={{ color: C.ink2, borderBottom: `1px solid ${C.line}` }}>{r.canal || "—"}</td>
                          <td className="py-2 text-right" style={{ color: C.ink2, borderBottom: `1px solid ${C.line}` }}>{r.pax}</td>
                          <td className="py-2 text-right font-bold" style={{ color: C.ink, borderBottom: `1px solid ${C.line}` }}>{fmtFull(r.venta)}</td>
                          {canSeeUtl && (
                            <td className="py-2 text-right" style={{ color: r.utilidad >= 0 ? C.ink2 : C.alert, borderBottom: `1px solid ${C.line}` }}>{fmtFull(r.utilidad)}</td>
                          )}
                          {detail.cxc_total != null && (
                            <>
                              <td className="py-2 text-right font-bold" style={{ color: r.vencido ? C.alert : C.ink, borderBottom: `1px solid ${C.line}` }}>{fmtFull(r.cxc_pend)}</td>
                              <td className="py-2 text-right" style={{ borderBottom: `1px solid ${C.line}` }}>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={r.vencido ? { background: "rgba(255,122,104,.14)", color: C.alert } : { background: "rgba(77,195,255,.12)", color: C.skyline }}>
                                  {r.vencido ? "Vencido" : (r.cxc_status || "Por vencer")}
                                </span>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {detail.truncado && <p className="text-[11px] pt-3" style={{ color: C.ink3 }}>Mostrando los 300 folios de mayor venta de {detail.count} totales.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <style>{`
        .dc-chip{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:700;padding:5px 11px;border-radius:999px;background:rgba(10,18,48,.4);color:#fff}
        .dc-chip small{font-weight:400;color:rgba(255,255,255,.7);font-size:10.5px}
        .dc-d{font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;margin-left:6px;white-space:nowrap}
        .dc-td{text-align:right;padding:10px 0 10px 16px;border-top:1px solid rgba(148,170,255,.14);color:#9aa7d4}
        .dc-rank{display:inline-flex;width:22px;height:22px;border-radius:6px;background:rgba(0,147,232,.15);color:#4dc3ff;font-size:11px;font-weight:700;align-items:center;justify-content:center;margin-right:10px}
        .dc-date{color-scheme:dark}
        .dc-date::-webkit-calendar-picker-indicator{cursor:pointer;opacity:.7}
      `}</style>
    </div>
  );
}
